import {IntlShape} from 'react-intl';

export type XeroDefaultTaxRate = {
	name: string | null;
	effectiveRate: number | null;
	organisation: string | null;
	/** The Xero TaxType code each export line carries when the default applies (T4-198). */
	externalId: string | null;
};

/** The client default every Export to Xero line takes: its rate, and the code the export sends. */
export type AppliedDefaultTax = {name: string | null; rate: number; taxType: string};

export type DefaultTaxRateDisplay = {kind: 'UNKNOWN'} | {kind: 'NONE'} | {kind: 'SET'; label: string};

/*
 * The one place a Xero percentage is written. Through intl, not interpolation: `${rate}%` prints
 * 12.5% in every locale where da and fr write 12,5 %, and `maximumFractionDigits` stops a real
 * 12.5% rate rounding to 13%. `rate` is in percent (20 = 20%).
 */
export const formatTaxPercent = (formatNumber: IntlShape['formatNumber'], rate: number): string =>
	formatNumber(rate / 100, {style: 'percent', maximumFractionDigits: 2});

// Xero tenant and contact ids are GUIDs whose casing and padding are not guaranteed to match between sources.
const normaliseXeroId = (id?: string | null): string => (id ?? '').trim().toLowerCase();

// The one comparison rule for Xero ids: tenants and contacts alike.
export const sameXeroId = (a?: string | null, b?: string | null): boolean => {
	const left = normaliseXeroId(a);
	const right = normaliseXeroId(b);
	return left !== '' && left === right;
};

export const sameTenant = sameXeroId;

/*
 * A default belongs to one Xero contact. On the Bill To path the export goes to the Bill To's own
 * contact, so the client's default applies only when that contact is the client's linked one.
 */
export const exportsToClientContact = (billToExternalId?: string | null, clientXeroContactId?: string | null): boolean =>
	sameXeroId(billToExternalId, clientXeroContactId);

/*
 * Whether the rate's own name already states the rate: any "<number> %" in it that equals the
 * rate. Both `12,5` and `12.5` are read as 12.5, and a space before % is allowed.
 */
export const nameShowsRate = (name: string, rate: number): boolean => {
	const target = Math.round(rate * 100);
	for (const match of name.matchAll(/(\d+(?:[.,]\d+)?)\s*%/g)) {
		if (Math.round(Number(match[1].replace(',', '.')) * 100) === target) {
			return true;
		}
	}
	return false;
};

/*
 * The client default every line takes, or null when each line takes its account's tax (T4-198).
 * The one rule for "a default applies": the header and the lines both read it, so they can't disagree.
 * A default without a rate can't be totalled, and one without a code can't be exported as itself.
 */
export const appliedDefaultTax = (
	rate: XeroDefaultTaxRate | null | undefined,
	organisation: string | null | undefined
): AppliedDefaultTax | null => {
	if (!organisation || !rate || !sameTenant(rate.organisation, organisation)) {
		return null;
	}
	const taxType = rate.externalId?.trim();
	if (rate.effectiveRate == null || !taxType) {
		return null;
	}
	return {name: rate.name, rate: rate.effectiveRate, taxType};
};

/*
 * What the export header shows for the client's Xero default tax rate.
 * UNKNOWN: no organisation is selected, so the rate cannot be judged.
 * NONE: no default the lines can apply (`appliedDefaultTax`): none, another organisation's, or one
 * missing its rate or its code.
 * SET: the rate's name, with " (<pct>)" appended unless the name already states the rate.
 */
export const defaultTaxRateDisplay = (
	rate: XeroDefaultTaxRate | null | undefined,
	organisation: string | null | undefined,
	formatPercent: (rate: number) => string
): DefaultTaxRateDisplay => {
	if (!organisation) {
		return {kind: 'UNKNOWN'};
	}
	const applied = appliedDefaultTax(rate, organisation);
	if (!applied) {
		return {kind: 'NONE'};
	}
	const name = applied.name?.trim();
	const pct = formatPercent(applied.rate);
	if (!name) {
		return {kind: 'SET', label: `(${pct})`};
	}
	return {kind: 'SET', label: nameShowsRate(name, applied.rate) ? name : `${name} (${pct})`};
};
